import { action, computed, makeObservable, observable } from "mobx"

import type { ManualClPoolStore } from "@/domains/thick-v2/manual-cl-store"
import type { ThickV2TickDistributionStore } from "@/domains/thick-v2/tick-distribution"
import type { PoolIntervalItem } from "@/domains/validators/graphql-service/pools"
import {
  concentratedApr,
  getAmountsForLiquidity,
  tickToPriceString,
  tryParseTickFromPrice,
} from "@/domains/manual-cl-core"
import { resolveCoherentSqrtPriceX96 } from "@/domains/thick-v2/utils/resolve-coherent-sqrt-price"

/**
 * Canonical DAO shape for constructing a `ManualClPositionStore`. All fields
 * come from the GraphQL `getMyPositions` query; on-chain data (pending fees,
 * current underlying amounts) is merged in later by the orchestrator via
 * `applyOnChainStats`.
 *
 * `positionId` is the composite GraphQL id (`<owner>-<pool>-<tickLower>-<tickUpper>-<chainId>`)
 * and is the identity the repository keys positions by.
 *
 * `tokenId` is the opaque routing / lookup key stored on the instance. When
 * `nfpmTokenId` is available (indexer Proposal 4) it mirrors that real on-chain
 * NFP tokenId; otherwise the orchestrator supplies a deterministic synthetic
 * BigInt so per-position routes and collections stay unique. Call sites that
 * need the actual NFPM contract token id must read `nfpmTokenId` and branch
 * on `nfpmTokenIdPending`.
 */
export type ManualClPositionStoreDAO = {
  positionId: string
  tokenId: bigint
  nfpmTokenId: bigint | undefined
  pool: ManualClPoolStore
  tickLower: number
  tickUpper: number
  liquidity: bigint
  hasLiquidity: boolean
  collectedFeesToken0: number
  collectedFeesToken1: number
  totalCollectedUsd: number
  totalDepositedToken0: number
  totalDepositedToken1: number
  totalWithdrawnToken0: number
  totalWithdrawnToken1: number
  createdAtTimestamp: number
  lastActivityTimestamp: number
}

export interface ManualClPositionAprDisplay {
  aprPct: number | undefined
  formattedPct: string
  formattedDailyUsd: string
  label: "Accrued" | "Current" | "Pool" | "Out of range" | "No pricing"
  variant: "realised" | "estimated"
  status: "ready" | "out-of-range" | "unindexed-pair"
}

/**
 * Observable state for a single Uniswap-v3-style position NFT.
 *
 * Instances are owned by `ManualClPositionRepository`. The position holds
 * a reference to the `ManualClPoolStore` rather than embedding pool data, so
 * any reactive consumer automatically re-renders when pool-level observables
 * (current tick, current sqrtPriceX96) change.
 *
 * Computed properties favour bit-exact math from `manual-cl-core`:
 *
 *   - `underlyingAmount0/1` derive from `getAmountsForLiquidity` against the
 *     pool's current sqrtPriceX96 (falling back to tick-derived sqrtRatio when
 *     slot0 has not yet been fetched).
 *   - `pendingFees0/1` are the last known simulated-collect amounts from the
 *     on-chain transport; the orchestrator refreshes them on a cadence and
 *     on every lifecycle mutation.
 *   - `isInRange` is the single source of truth consumed by UI badges.
 */
export class ManualClPositionStore {
  /**
   * Composite GraphQL id — `<owner>-<pool>-<tickLower>-<tickUpper>-<chainId>`.
   * Stable across `IncreaseLiquidity` / `DecreaseLiquidity` / `Collect` and
   * independent of any NFPM token id minted against the same tick range.
   */
  readonly positionId: string
  /**
   * Opaque routing key — either the real on-chain NFP `tokenId` when
   * `nfpmTokenId` is known, or a deterministic synthetic bigint generated by
   * the orchestrator. Always stable for a given `positionId`.
   */
  readonly tokenId: bigint
  /**
   * The real on-chain NFPM `tokenId` (Proposal 4). Undefined until the
   * indexer exposes `Position.nfpmTokenIds` — call sites that hit NFPM
   * contract methods must branch on `nfpmTokenIdPending`.
   */
  nfpmTokenId: bigint | undefined
  pool: ManualClPoolStore
  tickLower: number
  tickUpper: number
  liquidity: bigint
  hasLiquidity: boolean
  collectedFeesToken0: number
  collectedFeesToken1: number
  totalCollectedUsd: number
  totalDepositedToken0: number
  totalDepositedToken1: number
  totalWithdrawnToken0: number
  totalWithdrawnToken1: number
  createdAtTimestamp: number
  lastActivityTimestamp: number

  /**
   * Last observed on-chain pending fees, populated by
   * `ManualClPositionOnChainTransport` via a simulated `collect` call. Start
   * at `undefined` so views can distinguish "not yet loaded" from "zero".
   */
  pendingFees0: bigint | undefined = undefined
  pendingFees1: bigint | undefined = undefined

  /**
   * Underlying token balances derived from a simulated `decreaseLiquidity`
   * call. Acts as a cross-check against `underlyingAmount0/1` computed from
   * liquidity math — when `currentSqrtPriceX96` is fresh the two should
   * agree within a couple of wei.
   */
  simulatedUnderlying0: bigint | undefined = undefined
  simulatedUnderlying1: bigint | undefined = undefined

  isBurned: boolean = false

  /**
   * Per-token USD price captured at each graphql refresh, derived from
   * `Token.derivedETH × Bundle.ethPriceUSD`. Stored on the position itself
   * so `positionValueUsd` can render the moment the row arrives, without
   * waiting on the token store's async `onChainPrice` to propagate (or
   * losing to a race where `bestPrice` is still `0`). Treated as an
   * authoritative override in the price-resolution fallback below.
   */
  token0UsdPriceFromGraph: number | undefined = undefined
  token1UsdPriceFromGraph: number | undefined = undefined

  /**
   * Optional reference to the thick-v2 tick-distribution store, injected by
   * the namespace factory so per-position forward-looking APR (`currentRate`)
   * has a pool-wide active-liquidity denominator to compute its fees-share
   * against.
   *
   * The distribution store tracks a single pool at a time (the one the user
   * is currently browsing), so `currentRate` can only produce a meaningful
   * number when `tickDistributionStore.activePoolAddress` matches this
   * position's pool. Otherwise it falls back to `undefined`, which the UI
   * translates to `—` rather than a misleading figure.
   */
  private tickDistributionStore: ThickV2TickDistributionStore | undefined

  constructor(data: ManualClPositionStoreDAO, tickDistributionStore?: ThickV2TickDistributionStore) {
    this.positionId = data.positionId
    this.tokenId = data.tokenId
    this.nfpmTokenId = data.nfpmTokenId
    this.pool = data.pool
    this.tickDistributionStore = tickDistributionStore
    this.tickLower = data.tickLower
    this.tickUpper = data.tickUpper
    this.liquidity = data.liquidity
    this.hasLiquidity = data.hasLiquidity
    this.collectedFeesToken0 = data.collectedFeesToken0
    this.collectedFeesToken1 = data.collectedFeesToken1
    this.totalCollectedUsd = data.totalCollectedUsd
    this.totalDepositedToken0 = data.totalDepositedToken0
    this.totalDepositedToken1 = data.totalDepositedToken1
    this.totalWithdrawnToken0 = data.totalWithdrawnToken0
    this.totalWithdrawnToken1 = data.totalWithdrawnToken1
    this.createdAtTimestamp = data.createdAtTimestamp
    this.lastActivityTimestamp = data.lastActivityTimestamp

    makeObservable(this, {
      pool: observable,
      nfpmTokenId: observable,
      tickLower: observable,
      tickUpper: observable,
      liquidity: observable,
      hasLiquidity: observable,
      collectedFeesToken0: observable,
      collectedFeesToken1: observable,
      totalCollectedUsd: observable,
      totalDepositedToken0: observable,
      totalDepositedToken1: observable,
      totalWithdrawnToken0: observable,
      totalWithdrawnToken1: observable,
      lastActivityTimestamp: observable,
      pendingFees0: observable,
      pendingFees1: observable,
      simulatedUnderlying0: observable,
      simulatedUnderlying1: observable,
      isBurned: observable,
      token0UsdPriceFromGraph: observable,
      token1UsdPriceFromGraph: observable,
      updateFromGraphQl: action,
      applyOnChainStats: action,
      applyIndexerNfpmSnapshot: action,
      markBurned: action,
      setNfpmTokenId: action,
      setGraphPrices: action,
      isInRange: computed,
      underlyingAmount0: computed,
      underlyingAmount1: computed,
      underlyingAmount0Usd: computed,
      underlyingAmount1Usd: computed,
      token0UsdPrice: computed,
      token1UsdPrice: computed,
      positionValueUsd: computed,
      pendingFees0Usd: computed,
      pendingFees1Usd: computed,
      claimableFeesUsd: computed,
      earnedFeesToken0: computed,
      earnedFeesToken1: computed,
      earnedFeesUsd: computed,
      netDepositedUsd: computed,
      roiPct: computed,
      netPnlUsd: computed,
      nfpmTokenIdPending: computed,
      displayRange: computed,
      priceLower: computed,
      priceUpper: computed,
      ageDays: computed,
      realisedApr: computed,
      currentRate: computed,
      bestAprDisplay: computed,
      timeInRangePct7d: computed,
      timeInRangeSampleHours: computed,
    })
  }

  /**
   * `true` while the indexer has not yet populated `nfpmTokenIds` for this
   * position (pre-Proposal 4, or an edge case where the entity exists but
   * the NFPM mint has not been indexed yet). UI consumers should render a
   * yellow warning triangle and disable NFPM contract interactions.
   */
  get nfpmTokenIdPending(): boolean {
    return this.nfpmTokenId === undefined
  }

  /**
   * Best-effort sqrtPriceX96 sourced from the pool. Prefers the fresh on-chain
   * slot0 read; falls back to tick-derived sqrtRatio so liquidity math still
   * renders sensible values during the initial load window.
   */
  private get resolvedSqrtPriceX96(): bigint | undefined {
    return resolveCoherentSqrtPriceX96({
      currentSqrtPriceX96: this.pool.currentSqrtPriceX96,
      currentTick: this.pool.currentTick,
    })
  }

  get isInRange(): boolean {
    if (this.pool.currentTick === undefined) return false
    return this.pool.currentTick >= this.tickLower && this.pool.currentTick < this.tickUpper
  }

  get underlyingAmount0(): bigint {
    const sqrtPriceX96 = this.resolvedSqrtPriceX96
    if (this.liquidity === 0n) return 0n
    if (sqrtPriceX96 === undefined) return this.simulatedUnderlying0 ?? 0n
    try {
      const { amount0 } = getAmountsForLiquidity({
        sqrtPriceX96,
        tickLower: this.tickLower,
        tickUpper: this.tickUpper,
        liquidity: this.liquidity,
      })
      return amount0
    } catch {
      return this.simulatedUnderlying0 ?? 0n
    }
  }

  get underlyingAmount1(): bigint {
    const sqrtPriceX96 = this.resolvedSqrtPriceX96
    if (this.liquidity === 0n) return 0n
    if (sqrtPriceX96 === undefined) return this.simulatedUnderlying1 ?? 0n
    try {
      const { amount1 } = getAmountsForLiquidity({
        sqrtPriceX96,
        tickLower: this.tickLower,
        tickUpper: this.tickUpper,
        liquidity: this.liquidity,
      })
      return amount1
    } catch {
      return this.simulatedUnderlying1 ?? 0n
    }
  }

  /**
   * Best-available USD price for token0, preferring the graph-derived
   * snapshot (`derivedETH × ethPriceUSD`) captured on the position so this
   * computation never depends on the token store's async price pipeline
   * reaching it in time. Falls back to the token's own `bestPrice` chain
   * (on-chain → debank → api) when the graph snapshot is missing.
   */
  get token0UsdPrice(): number {
    const fromGraph = this.token0UsdPriceFromGraph
    if (fromGraph !== undefined && fromGraph > 0 && Number.isFinite(fromGraph)) {
      return fromGraph
    }
    return this.pool.token0.bestPrice
  }

  get token1UsdPrice(): number {
    const fromGraph = this.token1UsdPriceFromGraph
    if (fromGraph !== undefined && fromGraph > 0 && Number.isFinite(fromGraph)) {
      return fromGraph
    }
    return this.pool.token1.bestPrice
  }

  /** Underlying token0 amount priced at the best-known USD price. */
  get underlyingAmount0Usd(): number {
    const v = Number(this.pool.token0.formatUnits(this.underlyingAmount0)) * this.token0UsdPrice
    return Number.isFinite(v) ? v : 0
  }

  /** Underlying token1 amount priced at the best-known USD price. */
  get underlyingAmount1Usd(): number {
    const v = Number(this.pool.token1.formatUnits(this.underlyingAmount1)) * this.token1UsdPrice
    return Number.isFinite(v) ? v : 0
  }

  get positionValueUsd(): number {
    return this.underlyingAmount0Usd + this.underlyingAmount1Usd
  }

  /**
   * Per-token USD breakdown of the pending (simulated-`collect`) fees. Returns
   * `undefined` when the stats RPC has not yet populated `pendingFees0/1` so
   * callers can render a "pending" state instead of a deceptive `$0`.
   */
  get pendingFees0Usd(): number | undefined {
    if (this.pendingFees0 === undefined) return undefined
    const v = Number(this.pool.token0.formatUnits(this.pendingFees0)) * this.token0UsdPrice
    return Number.isFinite(v) ? v : 0
  }

  get pendingFees1Usd(): number | undefined {
    if (this.pendingFees1 === undefined) return undefined
    const v = Number(this.pool.token1.formatUnits(this.pendingFees1)) * this.token1UsdPrice
    return Number.isFinite(v) ? v : 0
  }

  get claimableFeesUsd(): number {
    const f0 = this.pendingFees0Usd
    const f1 = this.pendingFees1Usd
    if (f0 === undefined || f1 === undefined) return 0
    return f0 + f1
  }

  /**
   * Lifetime fees earned in token0 as a human-readable number.
   *
   * Fees = total collected - total withdrawn.
   *
   * Rationale: the indexer reports `totalCollected` as everything collected
   * from the NFPM, including principal that came from burns. Subtracting
   * `totalWithdrawn` removes that principal and leaves the fee component.
   */
  get earnedFeesToken0(): number {
    const earned = this.totalCollectedToken0Adjusted
    return earned > 0 ? earned : 0
  }

  get earnedFeesToken1(): number {
    const earned = this.totalCollectedToken1Adjusted
    return earned > 0 ? earned : 0
  }

  /**
   * Fee reconstruction: positive when the user has collected more than they
   * "should have" from withdrawals alone — which is the fee component. We
   * clamp to zero elsewhere but keep the raw figure here for testing.
   */
  private get totalCollectedToken0Adjusted(): number {
    return this.collectedFeesToken0 - this.totalWithdrawnToken0
  }

  private get totalCollectedToken1Adjusted(): number {
    return this.collectedFeesToken1 - this.totalWithdrawnToken1
  }

  /**
   * Lifetime earned fees in USD. Uses current token prices — purely a
   * display figure, not a ledger.
   */
  get earnedFeesUsd(): number {
    const usd0 = this.earnedFeesToken0 * this.token0UsdPrice
    const usd1 = this.earnedFeesToken1 * this.token1UsdPrice
    if (!Number.isFinite(usd0) || !Number.isFinite(usd1)) return 0
    return usd0 + usd1
  }

  /**
   * Net USD capital currently deployed in the position — deposits minus
   * principal withdrawals, valued at current token prices.
   */
  get netDepositedUsd(): number {
    const price0 = this.token0UsdPrice
    const price1 = this.token1UsdPrice
    const deposited0 = this.totalDepositedToken0 * price0
    const deposited1 = this.totalDepositedToken1 * price1
    const withdrawn0 = this.totalWithdrawnToken0 * price0
    const withdrawn1 = this.totalWithdrawnToken1 * price1
    const net = deposited0 + deposited1 - withdrawn0 - withdrawn1
    return Number.isFinite(net) ? net : 0
  }

  /**
   * Percentage return on capital, based on earned fees divided by the total
   * USD deposited across the lifetime of the position. Returns `undefined`
   * when deposits are zero so callers can render `—` rather than Infinity.
   */
  get roiPct(): number | undefined {
    const depositedUsd =
      this.totalDepositedToken0 * this.token0UsdPrice + this.totalDepositedToken1 * this.token1UsdPrice
    if (!Number.isFinite(depositedUsd) || depositedUsd <= 0) return undefined
    return (this.earnedFeesUsd / depositedUsd) * 100
  }

  /**
   * Headline PnL figure: current position value plus what has already been
   * withdrawn / collected, minus total capital ever deposited. Positive
   * numbers mean the position is in profit; negative means impermanent loss
   * has outpaced fees.
   */
  get netPnlUsd(): number {
    const price0 = this.token0UsdPrice
    const price1 = this.token1UsdPrice
    const currentValue = this.positionValueUsd
    const withdrawn0 = this.totalWithdrawnToken0 * price0
    const withdrawn1 = this.totalWithdrawnToken1 * price1
    const deposited0 = this.totalDepositedToken0 * price0
    const deposited1 = this.totalDepositedToken1 * price1
    const pnl = currentValue + withdrawn0 + withdrawn1 + this.earnedFeesUsd - deposited0 - deposited1
    return Number.isFinite(pnl) ? pnl : 0
  }

  get priceLower(): string {
    try {
      return tickToPriceString({
        tick: this.tickLower,
        token0: this.pool.token0,
        token1: this.pool.token1,
        decimalPlaces: 6,
      })
    } catch {
      return ""
    }
  }

  get priceUpper(): string {
    try {
      return tickToPriceString({
        tick: this.tickUpper,
        token0: this.pool.token0,
        token1: this.pool.token1,
        decimalPlaces: 6,
      })
    } catch {
      return ""
    }
  }

  get displayRange(): string {
    const lower = this.priceLower
    const upper = this.priceUpper
    if (!lower || !upper) return "—"
    return `${lower} – ${upper}`
  }

  /**
   * Refresh the mutable fields from a fresh GraphQL row. Identity
   * (`positionId`, `tokenId`, `pool`) never changes; `nfpmTokenId` may flip
   * from undefined → bigint when Proposal 4 lands, via {@link setNfpmTokenId}.
   */
  updateFromGraphQl(data: Omit<ManualClPositionStoreDAO, "positionId" | "tokenId" | "nfpmTokenId" | "pool">): void {
    this.tickLower = data.tickLower
    this.tickUpper = data.tickUpper
    this.liquidity = data.liquidity
    this.hasLiquidity = data.hasLiquidity
    this.collectedFeesToken0 = data.collectedFeesToken0
    this.collectedFeesToken1 = data.collectedFeesToken1
    this.totalCollectedUsd = data.totalCollectedUsd
    this.totalDepositedToken0 = data.totalDepositedToken0
    this.totalDepositedToken1 = data.totalDepositedToken1
    this.totalWithdrawnToken0 = data.totalWithdrawnToken0
    this.totalWithdrawnToken1 = data.totalWithdrawnToken1
    this.lastActivityTimestamp = data.lastActivityTimestamp
  }

  /**
   * Promote an indexed `nfpmTokenId` onto the store once Proposal 4 starts
   * returning values. Idempotent: repeated calls with the same id are a
   * no-op.
   */
  setNfpmTokenId(nfpmTokenId: bigint | undefined): void {
    this.nfpmTokenId = nfpmTokenId
  }

  /**
   * Persist per-token USD prices derived from the indexer
   * (`Token.derivedETH × Bundle.ethPriceUSD`) directly on the position.
   * This is the authoritative price source for `positionValueUsd` and
   * friends — we don't wait for the shared token store to backfill. Zero
   * or non-finite inputs are ignored so a bad snapshot never overwrites a
   * good one.
   */
  setGraphPrices(token0UsdPrice: number | undefined, token1UsdPrice: number | undefined): void {
    if (token0UsdPrice !== undefined && Number.isFinite(token0UsdPrice) && token0UsdPrice > 0) {
      this.token0UsdPriceFromGraph = token0UsdPrice
    }
    if (token1UsdPrice !== undefined && Number.isFinite(token1UsdPrice) && token1UsdPrice > 0) {
      this.token1UsdPriceFromGraph = token1UsdPrice
    }
  }

  applyOnChainStats(stats: {
    pendingFees0: bigint
    pendingFees1: bigint
    underlying0?: bigint
    underlying1?: bigint
  }): void {
    this.pendingFees0 = stats.pendingFees0
    this.pendingFees1 = stats.pendingFees1
    if (stats.underlying0 !== undefined) {
      this.simulatedUnderlying0 = stats.underlying0
    }
    if (stats.underlying1 !== undefined) {
      this.simulatedUnderlying1 = stats.underlying1
    }
  }

  /**
   * Merge an indexer-native `NfpmPosition` row onto the store. The indexer
   * already runs `getAmountsForLiquidity` against the pool's `sqrtPrice` —
   * `principal0/1` are the same underlying amounts the on-chain simulated
   * `decreaseLiquidity` would return, just without the RPC round-trip. Seeding
   * them here lets the overview + remove-liquidity math render correctly on
   * first paint, before (or even without) the stats RPC firing.
   *
   * `pendingFees0/1` are intentionally untouched — those come from
   * `tokensOwed`, which the indexer cannot derive from events alone.
   */
  applyIndexerNfpmSnapshot(snapshot: {
    nfpmTokenId: bigint
    liquidity: bigint
    principal0: bigint
    principal1: bigint
    totalCollectedToken0?: number
    totalCollectedToken1?: number
    totalWithdrawnToken0?: number
    totalWithdrawnToken1?: number
  }): void {
    this.nfpmTokenId = snapshot.nfpmTokenId
    this.liquidity = snapshot.liquidity
    this.hasLiquidity = snapshot.liquidity > 0n
    const verifiedPrincipal = this.resolveVerifiedPrincipal(snapshot)
    this.simulatedUnderlying0 = verifiedPrincipal.principal0
    this.simulatedUnderlying1 = verifiedPrincipal.principal1
    if (snapshot.totalCollectedToken0 !== undefined) {
      this.collectedFeesToken0 = snapshot.totalCollectedToken0
    }
    if (snapshot.totalCollectedToken1 !== undefined) {
      this.collectedFeesToken1 = snapshot.totalCollectedToken1
    }
    if (snapshot.totalWithdrawnToken0 !== undefined) {
      this.totalWithdrawnToken0 = snapshot.totalWithdrawnToken0
    }
    if (snapshot.totalWithdrawnToken1 !== undefined) {
      this.totalWithdrawnToken1 = snapshot.totalWithdrawnToken1
    }
  }

  markBurned(): void {
    this.isBurned = true
    this.liquidity = 0n
    this.hasLiquidity = false
  }

  private resolveVerifiedPrincipal(snapshot: {
    liquidity: bigint
    principal0: bigint
    principal1: bigint
  }): { principal0: bigint; principal1: bigint } {
    const sqrtPriceX96 = this.resolvedSqrtPriceX96
    if (sqrtPriceX96 === undefined) {
      return {
        principal0: snapshot.principal0,
        principal1: snapshot.principal1,
      }
    }
    try {
      const { amount0, amount1 } = getAmountsForLiquidity({
        sqrtPriceX96,
        tickLower: this.tickLower,
        tickUpper: this.tickUpper,
        liquidity: snapshot.liquidity,
      })
      return { principal0: amount0, principal1: amount1 }
    } catch {
      return {
        principal0: snapshot.principal0,
        principal1: snapshot.principal1,
      }
    }
  }

  /**
   * Age of the position in days, measured from the on-chain `createdAtTimestamp`
   * (unix seconds) to the current wall clock. Used as the denominator in
   * {@link realisedApr}.
   */
  get ageDays(): number {
    if (!this.createdAtTimestamp || this.createdAtTimestamp <= 0) return 0
    const nowSec = Date.now() / 1000
    const ageSec = nowSec - this.createdAtTimestamp
    return ageSec > 0 ? ageSec / 86_400 : 0
  }

  /**
   * Accrued APR: what the position has earned or can currently collect
   * since inception. Formula is the textbook annualisation of accrued yield —
   * collected plus claimable fees divided by lifetime deposited capital,
   * divided by the age in days, scaled to a full year.
   *
   * We deliberately use lifetime deposited capital (`totalDepositedToken0/1 *
   * price`) rather than `positionValueUsd`:
   *
   *   - Burned / fully-withdrawn positions have `positionValueUsd === 0` but
   *     still carry a real history of earned fees; users expect to see the
   *     APR they realised before closing.
   *   - Positions that have been partially decreased would artificially
   *     inflate against current value (fees stayed, capital shrank).
   *   - `positionValueUsd` moves with impermanent loss, which makes the
   *     denominator wiggle every second even when no fees accrue.
   *
   * Very young positions use a one-hour minimum age denominator so a fresh
   * fee accrual can surface without exploding to infinity in the first few
   * blocks. Positions with no age, no deposits, or no accrued fees report
   * `undefined` + `"—"` so the UI renders a neutral dash.
   *
   * Distinct from {@link currentRate}, which is forward-looking and depends
   * on pool liquidity, not realised fees.
   */
  get realisedApr(): {
    aprPct: number | undefined
    formattedPct: string
    ageDays: number
  } {
    const ageDays = this.ageDays
    const accrued = this.earnedFeesUsd + this.claimableFeesUsd
    const depositedUsd =
      this.totalDepositedToken0 * this.token0UsdPrice + this.totalDepositedToken1 * this.token1UsdPrice
    if (
      ageDays <= 0 ||
      !Number.isFinite(depositedUsd) ||
      depositedUsd <= 0 ||
      !Number.isFinite(accrued) ||
      accrued <= 0
    ) {
      return { aprPct: undefined, formattedPct: "—", ageDays }
    }
    const effectiveAgeDays = Math.max(ageDays, 1 / 24)
    const aprPct = (accrued / depositedUsd / effectiveAgeDays) * 365 * 100
    if (!Number.isFinite(aprPct) || aprPct <= 0) {
      return { aprPct: undefined, formattedPct: "—", ageDays }
    }
    return { aprPct, formattedPct: formatAprPct(aprPct), ageDays }
  }

  /**
   * Forward-looking APR: what the position would earn if current pool
   * volume, current active liquidity, and the position's current tick
   * range all held steady. Delegates the math to the pure
   * `concentratedApr` function — this getter only assembles inputs.
   *
   * Returns a safe zero-result when the pool has no live tick / price,
   * when the position has no liquidity, or when the shared tick
   * distribution store is currently scoped to a different pool.
   */
  get currentRate(): {
    aprPct: number
    dailyFeesUsd: number
    inRange: boolean
    formattedPct: string
    formattedDailyUsd: string
  } {
    const zero = {
      aprPct: 0,
      dailyFeesUsd: 0,
      inRange: false,
      formattedPct: "0.00%",
      formattedDailyUsd: "",
    }

    const currentTick = this.pool.currentTick
    if (currentTick === undefined) return zero
    if (this.liquidity <= 0n) return zero
    const value = this.positionValueUsd
    if (value <= 0) return zero

    const totalActiveLiquidity = this.resolveActiveLiquidityAt(currentTick)

    const result = concentratedApr({
      currentTick,
      feeTierBps: this.pool.feeTier,
      volumeUsd24h: this.pool.volumeUsd24h,
      totalActiveLiquidity,
      tickLower: this.tickLower,
      tickUpper: this.tickUpper,
      positionLiquidity: this.liquidity,
      positionValueUsd: value,
      positionAlreadyInPool: true,
    })

    return {
      aprPct: result.aprPct,
      dailyFeesUsd: result.dailyFeesUsd,
      inRange: result.inRange,
      formattedPct: formatCurrentAprPct(result),
      formattedDailyUsd: formatDailyUsd(result.dailyFeesUsd),
    }
  }

  get bestAprDisplay(): ManualClPositionAprDisplay {
    const realised = this.realisedApr
    if (realised.aprPct !== undefined && hasAprValue(realised.formattedPct)) {
      return {
        aprPct: realised.aprPct,
        formattedPct: realised.formattedPct,
        formattedDailyUsd: "",
        label: "Accrued",
        variant: "realised",
        status: "ready",
      }
    }

    const current = this.currentRate
    if (current.inRange && current.aprPct > 0 && hasAprValue(current.formattedPct)) {
      return {
        aprPct: current.aprPct,
        formattedPct: current.formattedPct,
        formattedDailyUsd: current.formattedDailyUsd,
        label: "Current",
        variant: "estimated",
        status: "ready",
      }
    }

    if (!this.isInRange) {
      return {
        aprPct: undefined,
        formattedPct: "",
        formattedDailyUsd: "",
        label: "Out of range",
        variant: "estimated",
        status: "out-of-range",
      }
    }

    const poolApr = this.pool.rangeApr7d
    if (poolApr.aprPct > 0 && hasAprValue(poolApr.formattedPct)) {
      return {
        aprPct: poolApr.aprPct,
        formattedPct: poolApr.formattedPct,
        formattedDailyUsd: "",
        label: "Pool",
        variant: "estimated",
        status: "ready",
      }
    }

    return {
      aprPct: undefined,
      formattedPct: "",
      formattedDailyUsd: "",
      label: "No pricing",
      variant: "estimated",
      status: "unindexed-pair",
    }
  }

  /**
   * Percentage of the position's recent lifetime (capped at the last seven
   * days, at hourly resolution) during which the pool's traded price sat
   * inside this position's `[tickLower, tickUpper]` range. Uses each
   * `PoolHourData` row's low/high as the hour's price envelope — if that
   * envelope intersects the position's range, the hour counts as "in
   * range".
   *
   * Hourly rows that predate `createdAtTimestamp` are filtered out so a
   * brand-new position can't inherit the pool's pre-mint price history: a
   * five-minute-old position whose range currently contains the pool
   * price should show either "—" (no completed hour to sample yet) or a
   * fresh "100%", not a stale "50%" coming from last week.
   *
   * Orientation is auto-detected by majority vote across the recent hourly
   * closes: each row's close is parsed in both orientations and we count
   * which one lands closer to the pool's `currentTick`. A single anomalous
   * hour (e.g. an illiquid outlier print) can't flip orientation — the vote
   * needs half of the sampled rows to swing with it.
   *
   * Returns `undefined` when we have no usable hourly samples so UI can
   * render `—` rather than `0%`, which would be indistinguishable from a
   * position whose range has never been touched.
   */
  get timeInRangePct7d(): number | undefined {
    const sample = this.timeInRangeHourlySample
    if (sample === undefined) return undefined
    const currentTick = this.pool.currentTick
    if (currentTick === undefined) return undefined

    const token0 = this.pool.token0
    const token1 = this.pool.token1
    const tickSpacing = this.pool.tickSpacing ?? getFallbackTickSpacing(this.pool.feeTier) ?? 64

    const invert = this.detectInvertFromHourly({
      rows: sample,
      currentTick,
      token0,
      token1,
      tickSpacing,
    })

    let total = 0
    let inRange = 0
    for (const row of sample) {
      const low = row.low
      const high = row.high
      if (low === undefined || high === undefined) continue
      if (!Number.isFinite(low) || !Number.isFinite(high)) continue
      if (low <= 0 || high <= 0) continue

      const tLow = tryParseTickFromPrice({
        price: String(low),
        token0,
        token1,
        tickSpacing,
        invert,
      })
      const tHigh = tryParseTickFromPrice({
        price: String(high),
        token0,
        token1,
        tickSpacing,
        invert,
      })
      if (tLow === undefined || tHigh === undefined) continue

      const lo = Math.min(tLow, tHigh)
      const hi = Math.max(tLow, tHigh)
      total++
      if (hi >= this.tickLower && lo < this.tickUpper) inRange++
    }

    if (total === 0) return undefined
    return (inRange / total) * 100
  }

  /**
   * Number of hourly rows that actually feed `timeInRangePct7d`. Exposed so
   * the view layer can label the stat honestly — "X% of last 7d" when the
   * window is full, "X% since opened" when the position is younger than
   * seven days. Returns `undefined` when no hours are available (new
   * position mid-first-hour, or missing indexer data).
   */
  get timeInRangeSampleHours(): number | undefined {
    const sample = this.timeInRangeHourlySample
    if (sample === undefined || sample.length === 0) return undefined
    return sample.length
  }

  /**
   * Shared slice of hourly rows used by both `timeInRangePct7d` and the
   * window-hours getter. Filters out rows whose period ended before the
   * position was minted so we never attribute pre-existence price history
   * to a freshly opened position.
   */
  private get timeInRangeHourlySample(): Array<PoolIntervalItem> | undefined {
    const rows = this.pool.poolHourData
    if (!rows || rows.length === 0) return undefined

    const createdAt = this.createdAtTimestamp
    const HOUR_SECONDS = 3_600
    const MAX_HOURS = 24 * 7
    const withinLifetime =
      createdAt && createdAt > 0
        ? rows.filter((row) => {
            const start = row.periodStartUnix ?? row.date
            if (start === undefined) return false
            // An hour counts once its end has elapsed past the position's
            // mint: otherwise we'd average a half-hour of pre-position
            // price data into the stat.
            return start + HOUR_SECONDS > createdAt
          })
        : rows

    if (withinLifetime.length === 0) return undefined
    return withinLifetime.slice(0, MAX_HOURS)
  }

  private detectInvertFromHourly(args: {
    rows: Array<{ close?: number; open?: number | undefined }>
    currentTick: number
    token0: import("@/domains/tokens/token-store").TokenStore
    token1: import("@/domains/tokens/token-store").TokenStore
    tickSpacing: number
  }): boolean {
    const { rows, currentTick, token0, token1, tickSpacing } = args
    const MAX_VOTES = 24
    let invertVotes = 0
    let straightVotes = 0
    for (const row of rows) {
      if (invertVotes + straightVotes >= MAX_VOTES) break
      const price = row.close ?? row.open
      if (price === undefined || !Number.isFinite(price) || price <= 0) continue
      const tickA = tryParseTickFromPrice({
        price: String(price),
        token0,
        token1,
        tickSpacing,
        invert: false,
      })
      const tickB = tryParseTickFromPrice({
        price: String(price),
        token0,
        token1,
        tickSpacing,
        invert: true,
      })
      if (tickA === undefined && tickB === undefined) continue
      if (tickA === undefined) {
        invertVotes++
        continue
      }
      if (tickB === undefined) {
        straightVotes++
        continue
      }
      const distA = Math.abs(tickA - currentTick)
      const distB = Math.abs(tickB - currentTick)
      if (distB < distA) invertVotes++
      else straightVotes++
    }
    return invertVotes > straightVotes
  }

  /**
   * Best-available estimate of in-range virtual liquidity at `tickIdx`.
   *
   * Preferred source is the dedicated tick-distribution store, because it
   * can answer for any tick (not just the pool's current one) — the manage
   * page needs this when it renders alongside the liquidity chart.
   *
   * When that store is not scoped to this pool (the cross-pool
   * my-positions list never loads a full tick book), we fall back to
   * `pool.liquidity` — Envio's indexer maintains this field the same way
   * the on-chain `pool.liquidity()` slot is maintained, i.e. by applying
   * `liquidityNet` deltas on every tick crossing. Under steady state both
   * sources produce the same number at `currentTick`, so forward-looking
   * APRs agree between the manage page and the my-positions table.
   *
   * Final fallback is `0n`, which `concentratedApr` treats as "no
   * denominator available" and collapses to a safe zero result rather
   * than dividing by it.
   */
  private resolveActiveLiquidityAt(tickIdx: number): bigint {
    const store = this.tickDistributionStore
    const positionPool = this.pool.address?.raw?.toLowerCase?.()
    const activePool = store?.activePoolAddress?.toLowerCase?.()
    if (store && positionPool && activePool && positionPool === activePool) {
      const fromDistribution = store.activeLiquidityAt(tickIdx)
      if (fromDistribution > 0n) return fromDistribution
    }
    // Only the pool's *current* tick corresponds to `pool.liquidity`. For
    // the forward-looking `currentRate` this is exactly the tick we need;
    // for historical/arbitrary ticks we have no fallback and return 0n.
    if (tickIdx === this.pool.currentTick && this.pool.liquidity > 0n) {
      return this.pool.liquidity
    }
    return 0n
  }
}

function formatAprPct(aprPct: number): string {
  if (!Number.isFinite(aprPct) || aprPct <= 0) return "0.00%"
  if (aprPct < 0.01) return "<0.01%"
  return `${aprPct.toFixed(2)}%`
}

function formatCurrentAprPct(result: { aprPct: number; dailyFeesUsd: number; inRange: boolean }): string {
  if (result.inRange && (!Number.isFinite(result.dailyFeesUsd) || result.dailyFeesUsd <= 0)) {
    return "—"
  }
  return formatAprPct(result.aprPct)
}

function hasAprValue(value: string): boolean {
  return value.length > 0 && value !== "—" && value !== "0.00%"
}

function formatDailyUsd(dailyUsd: number): string {
  // Zero / unknown → empty string so the consumer can hide the entire
  // "≈ $0.00/day" fragment instead of rendering a meaningless zero.
  if (!Number.isFinite(dailyUsd) || dailyUsd <= 0) return ""
  if (dailyUsd < 0.01) return "< $0.01/day"
  if (dailyUsd < 1000) return `$${dailyUsd.toFixed(2)}/day`
  return `$${Math.round(dailyUsd).toLocaleString()}/day`
}

function getFallbackTickSpacing(feeTierBps: number): number | undefined {
  if (feeTierBps === 100) return 1
  if (feeTierBps === 500) return 10
  if (feeTierBps === 3_000) return 60
  if (feeTierBps === 10_000) return 64
  return undefined
}
